import { css } from 'astroturf';
import React, { useState } from 'react';

import Link from './link';
import Punchout from './Punchout';

const styles = css`
  .breakeven {
    max-width: 55rem;
    margin: 2rem auto 2rem;
    text-align: center;
  }

  .sentence {
    margin: 0;
    font-size: 1.0625rem;
    line-height: 2.1;
    color: #0a1530;
    text-wrap: pretty;

    & input[type='number'] {
      width: 3.75rem;
      background: #fff;
      padding: 0.15rem 0.4rem;
      border-radius: 5px;
      border: 1px solid rgba(0, 70, 150, 0.2);
      font-feature-settings: 'tnum';
      font-size: inherit;
      font-weight: 700;
      text-align: center;
      color: inherit;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &:focus-visible {
        outline: 2px solid #2c82ad;
        outline-offset: 1px;
      }
    }

    /* the duration is punched out; let it wrap on narrow screens */
    & span {
      white-space: normal;
      font-weight: 700;
    }

    & .nowrap {
      white-space: nowrap;
      font-weight: inherit;
    }
  }

  .footnote {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    line-height: 1.6;
    color: rgb(51, 51, 56);
  }
`;

const YEARLY_PER_MONTH = 9;

// Turns a duration in minutes into something you can picture.
const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return 'no time at all';
  }

  const totalSeconds = Math.round(minutes * 60);

  if (totalSeconds < 60) {
    return `${totalSeconds} seconds`;
  }

  if (totalSeconds >= 3600) {
    const hours = Math.floor(totalSeconds / 3600);
    const restMinutes = Math.round((totalSeconds % 3600) / 60);
    const hourLabel = `${hours} hour${hours === 1 ? '' : 's'}`;
    return restMinutes === 0 ? hourLabel : `${hourLabel} and ${restMinutes} minutes`;
  }

  const wholeMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const minuteLabel = `${wholeMinutes} minute${wholeMinutes === 1 ? '' : 's'}`;

  return seconds === 0 ? minuteLabel : `${minuteLabel} and ${seconds} seconds`;
};

// Keeps the raw field text (so the input can be empty while you retype) alongside the last
// usable number, so clearing a field does not flash a nonsense answer.
const useNumberField = (initial, min, max) => {
  const [field, setField] = useState({ raw: String(initial), value: initial });

  const onInput = (e) => {
    const { value: raw } = e.target;
    const parsed = parseInt(raw, 10);
    const usable = Number.isFinite(parsed) && parsed >= min;

    setField((prev) => ({ raw, value: usable ? Math.min(parsed, max) : prev.value }));
  };

  return [field.raw, field.value, onInput];
};

const BreakEven = ({ currency = '€' }) => {
  const [rawRate, safeRate, onRate] = useNumberField(60, 1, 100000);
  const [rawDays, safeDays, onDays] = useNumberField(4, 1, 31);

  const minutesPerMonth = (YEARLY_PER_MONTH / safeRate) * 60;
  const minutesPerSession = minutesPerMonth / safeDays;

  return (
    <div className={styles.breakeven}>
      <p className={styles.sentence}>
        Polypane is just {currency}9 a month on the yearly plan, so if an hour of
        your time is worth{' '}
        <span className={styles.nowrap}>
          {currency} <input type="number" min="0" step="5" value={rawRate} onInput={onRate} aria-label="Hourly rate" />
        </span>{' '}
        and you open it even just {' '}
        <input type="number" min="1" max="31" step="1" value={rawDays} onInput={onDays} aria-label="Days per month" />{' '}
        {safeDays === 1 ? 'day' : 'days'} a month, it only has to speed up your workflow by{' '}
        <Punchout>{formatDuration(minutesPerSession)}</Punchout> {safeDays === 1 ? 'that day' : 'on each of those days'}{' '}
        to save you more than it costs. Want to see your ROI too? <Link to="/calculator/">Run the calculator</Link>.
      </p>
    </div>
  );
};

export default BreakEven;
